import {Trans} from '@lingui/macro';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {TextBox} from '../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {ButtonGroupSpacer} from '../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {Dialog} from '../../../components/common/dialogs/dialog';
import {DialogContent} from '../../../components/common/dialogs/dialogContent';
import {DialogFooter} from '../../../components/common/dialogs/dialogFooter';
import {DialogHeader} from '../../../components/common/dialogs/dialogHeader';
import {useLinkNavigation} from '../../../components/common/navigation/useLinkNavigation';
import {useAppDispatch} from '../../../helpers/reduxHelpers';
import {ApplicationEntryPointTypesEnum} from '../../../models/api/applicationEntryPointModel';
import {LocalApplicationEntryPointModel} from '../../../models/api/localApplicationEntryPointModel';
import {LocalApplicationModel} from '../../../models/api/localApplicationModel';
import {buildSettingsDevelopersPath} from '../../../models/url/settingsUrlHelpers';
import {SettingsDevelopersPagesEnum, SettingsRootPagesEnum} from '../../../models/url/settingsUrlModel';
import {deleteLocalApplicationEntryPointAsyncAction} from '../../../store/data/localApplications/localApplicationsActions';
import {SettingsBannersEnum, useSettingsBanners} from '../../banners/settingsBannersContext';

/*
 * Constants.
 */

const deleteEntryPointToken = 'DELETE';

const entryPointDeleteDescription: {
  [key in ApplicationEntryPointTypesEnum]: React.ReactNode;
} = {
  [ApplicationEntryPointTypesEnum.INBOX_SIDE_PANEL]: (
    <Trans id="settings-developers-delete-inbox-side-panel-entry-point-dialog-description-disclaimer">
      Are you sure you want to permanently delete this plugin? Any users relying on the plugin will no longer
      be able to use it. Type {deleteEntryPointToken} to confirm this action.
    </Trans>
  ),
};

const entryPointDeleteTitle: {
  [key in ApplicationEntryPointTypesEnum]: React.ReactNode;
} = {
  [ApplicationEntryPointTypesEnum.INBOX_SIDE_PANEL]: (
    <Trans id="settings-developers-delete-inbox-side-panel-entry-point-dialog-title">Delete plugin</Trans>
  ),
};

const entryPointDeleteErrorMessage: {
  [key in ApplicationEntryPointTypesEnum]: React.ReactNode;
} = {
  [ApplicationEntryPointTypesEnum.INBOX_SIDE_PANEL]: (
    <Trans id="settings-developers-delete-inbox-side-panel-entry-point-error-banner">
      Error deleting plugin. Please try again.
    </Trans>
  ),
};

/*
 * Props.
 */

interface SettingsDevelopersDeleteEntryPointDialogProps {
  application: LocalApplicationModel;
  entryPoint: LocalApplicationEntryPointModel;
  onRequestClose: Handler;
}

/*
 * Style.
 */

const StyledButtonGroup = styled(ButtonGroup)`
  width: 100%;
`;

/*
 * Component.
 */

export const SettingsDevelopersDeleteEntryPointDialog: FC<SettingsDevelopersDeleteEntryPointDialogProps> = (
  props,
) => {
  const {entryPoint, application, onRequestClose} = props;
  const [deleteTextInput, setDeleteTextInput] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const {showBanner} = useSettingsBanners();
  const dispatch = useAppDispatch();
  const {navigateToLink} = useLinkNavigation();

  const onCloseDialog = () => {
    // If we are deleting, prevent closing.
    if (isDeleting) {
      return;
    }
    onRequestClose();
  };

  const navigateBackToFeatures = () => {
    navigateToLink(
      buildSettingsDevelopersPath({
        rootPage: SettingsRootPagesEnum.DEVELOPERS,
        subPage: SettingsDevelopersPagesEnum.APPLICATION_FEATURES,
        applicationId: application.id,
      }),
    );
  };

  const onDeleteClickAsync = async () => {
    setIsDeleting(true);
    try {
      await dispatch(deleteLocalApplicationEntryPointAsyncAction(application.id, entryPoint.id));
      navigateBackToFeatures();
      onRequestClose();
    } catch {
      showBanner({
        type: SettingsBannersEnum.ERROR,
        message: entryPointDeleteErrorMessage[entryPoint.type],
      });
      setIsDeleting(false);
    }
  };

  return (
    <Dialog onRequestClose={onCloseDialog}>
      <DialogHeader onRequestClose={onCloseDialog} title={entryPointDeleteTitle[entryPoint.type]} />
      <DialogContent>
        {entryPointDeleteDescription[entryPoint.type]}
        <TextBox value={deleteTextInput} onChange={setDeleteTextInput} placeholder={deleteEntryPointToken} />
      </DialogContent>
      <DialogFooter>
        <StyledButtonGroup>
          <ButtonGroupSpacer />
          <Button onClick={onCloseDialog} isDisabled={isDeleting}>
            <Trans id="settings-developers-delete-entry-point-dialog-cancel-button">Cancel</Trans>
          </Button>
          <Button
            variant={ButtonVariantsEnum.DANGER}
            onClick={onDeleteClickAsync}
            isDisabled={deleteTextInput !== deleteEntryPointToken || isDeleting}
          >
            <Trans id="settings-developers-delete-entry-point-dialog-delete-button">Delete</Trans>
          </Button>
        </StyledButtonGroup>
      </DialogFooter>
    </Dialog>
  );
};
